/**
 *
 * @param {ROICalculatorState} state
 * @returns {Calculation}
 */
export function calculateSavings(state) {
  const { roadCalls, fleetDetails, fuel, tires, maintenance } =
    state.formValues;

  const { calcConstants } = state;

  // Fuel Savings
  const { annualFuelSavingsFleetDollars, annualFuelSavingsPerTrailerDollars } =
    calculateFuelSavings(fleetDetails, fuel, calcConstants);

  // Road Call Savings
  const { roadCallSavingsFleetDollars, roadCallSavingsPerTrailerDollars } =
    calculateRoadCallSavings(roadCalls, fleetDetails);

  // Tire Savings
  const { tireSavingsForFleetDollars, tireSavingsPerTrailerDollars } =
    calculateTireSavings(fleetDetails, tires, calcConstants);

  // Maintenance Savings
  const {
    maintenanceSavingsFleetDollars,
    maintenanceSavingsPerTrailerDollars,
  } = calculateMaintenanceSavings(fleetDetails, maintenance);

  // Totals
  const tireSavingsPerMileCents =
    (tireSavingsForFleetDollars /
      fleetDetails.tractor.total /
      fleetDetails.tractor.annualDistance) *
    100;
  const fuelSavingsPerMileCents =
    (annualFuelSavingsFleetDollars /
      fleetDetails.tractor.total /
      fleetDetails.tractor.annualDistance) *
    100;
  const maintenanceSavingsPerMileCents =
    (maintenanceSavingsFleetDollars /
      fleetDetails.tractor.total /
      fleetDetails.tractor.annualDistance) *
    100;
  const roadCallSavingsPerMileCents =
    (roadCallSavingsFleetDollars /
      fleetDetails.tractor.total /
      fleetDetails.tractor.annualDistance) *
    100;

  const annualTrailerSavingsDollars =
    annualFuelSavingsPerTrailerDollars +
    roadCallSavingsPerTrailerDollars +
    tireSavingsPerTrailerDollars +
    maintenanceSavingsPerTrailerDollars;
  const annualFleetSavingsDollars =
    annualFuelSavingsFleetDollars +
    roadCallSavingsFleetDollars +
    tireSavingsForFleetDollars +
    maintenanceSavingsFleetDollars;

  const totalPerMileCents =
    fuelSavingsPerMileCents +
    roadCallSavingsPerMileCents +
    tireSavingsPerMileCents +
    maintenanceSavingsPerMileCents;

  const paybackOnInvestmentFleetMonths =
    (calcConstants.costOfTIREMAAXDollars * fleetDetails.trailer.total * 12) /
    annualFleetSavingsDollars;
  const paybackOnInvestmentTrailerMonths =
    (calcConstants.costOfTIREMAAXDollars * 12) / annualTrailerSavingsDollars;

  return {
    payback: {
      paybackOnInvestmentFleetMonths,
      paybackOnInvestmentTrailerMonths,
    },

    fleetTotals: {
      annualFleetSavingsDollars,
      annualFuelSavingsFleetDollars,
      roadCallSavingsFleetDollars,
      tireSavingsForFleetDollars,
      maintenanceSavingsFleetDollars,
    },

    trailerTotals: {
      annualTrailerSavingsDollars,
      annualFuelSavingsPerTrailerDollars,
      roadCallSavingsPerTrailerDollars,
      tireSavingsPerTrailerDollars,
      maintenanceSavingsPerTrailerDollars,
    },

    perMile: {
      fuelSavingsPerMileCents,
      roadCallSavingsPerMileCents,
      tireSavingsPerMileCents,
      maintenanceSavingsPerMileCents,
      totalPerMileCents,
    },
  };
}

/**
 * @param {FleetDetails} fleetDetails
 * @param {Fuel} fuel
 * @param {CalculationConstants} calcConstants
 * @returns {{annualFuelSavingsFleetDollars: number,annualFuelSavingsPerTrailerDollars: number }}
 */
function calculateFuelSavings(fleetDetails, fuel, calcConstants) {
  const annualFuelUsageGallons =
    fleetDetails.tractor.annualDistance / fuel.tractorMPG;
  const annualFuelSavingsGallons =
    annualFuelUsageGallons * calcConstants.fuelEfficiency;
  const annualFuelSavingsPerTractorDollars =
    annualFuelSavingsGallons * fuel.costOfFuel;

  const annualFuelSavingsFleetDollars =
    annualFuelSavingsPerTractorDollars * fleetDetails.tractor.total;
  const annualFuelSavingsPerTrailerDollars =
    annualFuelSavingsFleetDollars / fleetDetails.trailer.total;

  return { annualFuelSavingsFleetDollars, annualFuelSavingsPerTrailerDollars };
}

/**
 * @param {RoadCalls} roadCalls
 * @param {FleetDetails} fleetDetails
 * @returns {{roadCallSavingsPerTrailerDollars: number, roadCallSavingsFleetDollars: number }}
 */
function calculateRoadCallSavings(roadCalls, fleetDetails) {
  const flatTireBreakdownWithoutTIREMAAXDollars =
    roadCalls.tireRelatedBreakdownsPerYear *
    (roadCalls.downtimePerHourCost * roadCalls.downtimeRoadCallsHours +
      roadCalls.costOfRoadCalls +
      roadCalls.tireReplacementCost);
  const flatTireBreakdownWithTIREMAAXDollars =
    flatTireBreakdownWithoutTIREMAAXDollars * 0.2;

  const roadCallSavingsPerTrailerDollars =
    flatTireBreakdownWithoutTIREMAAXDollars -
    flatTireBreakdownWithTIREMAAXDollars;
  const roadCallSavingsFleetDollars =
    roadCallSavingsPerTrailerDollars * fleetDetails.trailer.total;

  return { roadCallSavingsPerTrailerDollars, roadCallSavingsFleetDollars };
}

/**
 * @param {FleetDetails} fleetDetails
 * @param {Tires} tires
 * @param {CalculationConstants} calcConstants
 * @returns {{tireSavingsPerTrailerDollars: number, tireSavingsForFleetDollars: number}}
 */
function calculateTireSavings(fleetDetails, tires, calcConstants) {
  const tirelifeWithTIREMAAXPROLB =
    tires.tireLife * calcConstants.TIREMAAXTireLifetimeImprovementRate +
    tires.tireLife;

  const costPerMileWithoutTIREMAAXDollars = tires.costOfTire / tires.tireLife;
  const costPerMileWithTIREMAAXDollars =
    tires.costOfTire / tirelifeWithTIREMAAXPROLB;
  const costPerYearPerTireWithoutTIREMAAXDollars =
    costPerMileWithoutTIREMAAXDollars * fleetDetails.trailer.annualDistance;
  const costPerYearPerTireWithTIREMAAXDollars =
    costPerMileWithTIREMAAXDollars * fleetDetails.trailer.annualDistance;

  const numberOfTires = tires.tireStyle === 'Super Single' ? 4 : 8;

  const costTiresTotalPerTrailerWithoutTIREMAAXDollars =
    numberOfTires * costPerYearPerTireWithoutTIREMAAXDollars;
  const costTiresTotalPerTrailerWithTIREMAAXDollars =
    numberOfTires * costPerYearPerTireWithTIREMAAXDollars;

  const tireSavingsPerTrailerDollars =
    costTiresTotalPerTrailerWithoutTIREMAAXDollars -
    costTiresTotalPerTrailerWithTIREMAAXDollars;
  const tireSavingsForFleetDollars =
    tireSavingsPerTrailerDollars * fleetDetails.trailer.total;

  return { tireSavingsForFleetDollars, tireSavingsPerTrailerDollars };
}

/**
 * @param {FleetDetails} fleetDetails
 * @param {Maintenance} maintenance
 * @returns {{maintenanceSavingsPerTrailerDollars: number, maintenanceSavingsFleetDollars: number}}
 */
function calculateMaintenanceSavings(fleetDetails, maintenance) {
  const costToCheckTirePressureWithoutTIREMAAX =
    (maintenance.laborTime * maintenance.laborRate) / 60;
  const timesTiresCheckedAnnuallyWithoutTIREMAAX =
    maintenance.numberOfChecks * 12;
  const annualCostToCheckTirePressureWithoutTIREMAAXDollars =
    costToCheckTirePressureWithoutTIREMAAX *
    timesTiresCheckedAnnuallyWithoutTIREMAAX;
  const annualCostToCheckTirePressureWithTIREMAAXDollars =
    costToCheckTirePressureWithoutTIREMAAX * 4;

  const maintenanceSavingsPerTrailerDollars =
    annualCostToCheckTirePressureWithoutTIREMAAXDollars -
    (annualCostToCheckTirePressureWithTIREMAAXDollars + 50);
  const maintenanceSavingsFleetDollars =
    maintenanceSavingsPerTrailerDollars * fleetDetails.trailer.total;

  return {
    maintenanceSavingsPerTrailerDollars,
    maintenanceSavingsFleetDollars,
  };
}

/**
 * @typedef {import('./state').ROICalculatorState} ROICalculatorState
 * @typedef {import('./state').Calculation} Calculation
 * @typedef {import('./state').FleetDetails} FleetDetails
 * @typedef {import('./state').Tires} Tires
 * @typedef {import('./state').Fuel} Fuel
 * @typedef {import('./state').Maintenance} Maintenance
 * @typedef {import('./state').RoadCalls} RoadCalls
 * @typedef {import('./state').CalculationConstants} CalculationConstants
 */
